import { Link } from "react-router-dom";
import { ShoppingCart, Menu, X, Search, User, LogOut } from "lucide-react";
import { useState } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";

const Header = () => {
  const { cartCount, categories } = useApp();
  const { isAuthenticated, user, logout } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <header className="sticky top-0 z-50 header-glass" data-testid="main-header">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="text-xl md:text-2xl font-bold text-primary">Mini</span>
            <span className="text-xl md:text-2xl font-bold text-foreground">Begum</span>
            <span className="text-xl md:text-2xl font-bold text-accent">Bazaar</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-sm font-medium text-foreground hover:text-primary transition-colors"
              data-testid="nav-home"
            >
              Home
            </Link>
            {categories.slice(0, 4).map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                data-testid={`nav-category-${category.slug}`}
              >
                {category.name}
              </Link>
            ))}
            <Link 
              to="/categories" 
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              data-testid="nav-all-categories"
            >
              All Categories
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Search */}
            <div className="hidden md:block">
              <form onSubmit={handleSearch} className="relative">
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-48 lg:w-64 pl-10 bg-muted/50 border-0"
                  data-testid="search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              </form>
            </div>

            {/* Mobile Search Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              data-testid="mobile-search-toggle"
            >
              <Search className="w-5 h-5" />
            </Button>

            {/* Account */}
            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/my-account" className="text-sm font-medium text-foreground hover:text-primary" data-testid="user-name-link">
                  Hi, {user?.name?.split(" ")[0]}
                </Link>
                <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Link to="/account" data-testid="account-link">
                <Button variant="ghost" size="icon" className="hidden md:flex">
                  <User className="w-5 h-5" />
                </Button>
              </Link>
            )}

            {/* Cart */}
            <Link to="/cart" className="relative" data-testid="cart-link">
              <Button variant="ghost" size="icon">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="cart-badge" data-testid="cart-badge">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-toggle">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle className="text-left">Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  <Link 
                    to="/" 
                    className="text-lg font-medium hover:text-primary transition-colors"
                    data-testid="mobile-nav-home"
                  >
                    Home
                  </Link>
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.slug}`}
                      className="text-lg font-medium hover:text-primary transition-colors"
                      data-testid={`mobile-nav-${category.slug}`}
                    >
                      {category.name}
                    </Link>
                  ))}
                  <hr className="my-4" />
                  {isAuthenticated ? (
                    <>
                      <Link to="/my-account" className="text-lg font-medium hover:text-primary transition-colors" data-testid="mobile-nav-account">
                        My Account
                      </Link>
                      <Link to="/track-order" className="text-lg font-medium hover:text-primary transition-colors" data-testid="mobile-nav-track">
                        Track Order
                      </Link>
                      <button onClick={logout} className="text-lg font-medium text-red-500 text-left" data-testid="mobile-nav-logout">
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link to="/account" className="text-lg font-medium hover:text-primary transition-colors" data-testid="mobile-nav-login">
                      Sign In / Register
                    </Link>
                  )}
                  <hr className="my-4" />
                  <Link 
                    to="/admin" 
                    className="text-lg font-medium hover:text-primary transition-colors"
                    data-testid="mobile-nav-admin"
                  >
                    Admin Panel
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {isSearchOpen && (
          <div className="md:hidden pb-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10"
                autoFocus
                data-testid="mobile-search-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </form>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
